import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { pickLocalized, useLanguage } from '@/features/i18n';
import { localizeRequestError } from '@/lib/localized-error';

import {
  ImpersonationSessionSyncError,
  useLogoutMutation,
  useStopImpersonationMutation,
} from '../api';
import { useAuthStore } from '../store';
import { useAccountTheme } from '../accountTheme';
import '../../../styles/auth-session.css';

interface AuthSessionBarProps {
  visible?: boolean;
}

/**
 * AuthSessionBar — 보호된 라우트 상단 shell.
 *
 * Information Architecture (Wave 2 / 트랙 B):
 *   - shell owner: language picker, logout, (cross-route 에 한해) 라이브러리 링크
 *   - shell 비-owner (page header 로 이관됨): 사용자명/이메일/크레딧 잔액,
 *     관리자 화면 진입, 초대 링크 생성, 페이지 요약.
 *
 * `/studio`, `/studio/:id` 와 같이 라이브러리 / 스튜디오 진입을 page header `<` 가
 * 담당하는 라우트에서는 shell 의 라이브러리 링크를 노출하지 않는다.
 */
export default function AuthSessionBar({ visible = true }: AuthSessionBarProps) {
  const { language } = useLanguage();
  const theme = useAccountTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logoutMutation = useLogoutMutation();
  const stopImpersonationMutation = useStopImpersonationMutation();
  const [failure, setFailure] = useState<{ action: 'logout' | 'return'; error: unknown } | null>(null);
  const [requiresAdminSyncRecovery, setRequiresAdminSyncRecovery] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setFailure(null);
    setMenuOpen(false);
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (user && !user.impersonation?.active) {
      setRequiresAdminSyncRecovery(false);
    }
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const menu = menuRef.current;
      if (!menu) return;
      if (event.target instanceof Node && !menu.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  if (requiresAdminSyncRecovery) {
    const recoveryText = {
      status: pickLocalized(language, {
        ko: '관리자 세션 전환 완료',
        en: 'Admin session restored',
        ja: '管理者セッションへの切り替え完了',
      }),
      description: pickLocalized(language, {
        ko: '관리자 세션으로 전환했지만 화면을 새로 확인하지 못했습니다. 관리자 화면을 다시 열어 복구된 세션을 확인해 주세요.',
        en: 'The admin session was restored, but the screen could not be refreshed. Reopen the admin page to verify the restored session.',
        ja: '管理者セッションへ切り替えましたが、画面を更新できませんでした。管理画面を開き直して、復元されたセッションを確認してください。',
      }),
      action: pickLocalized(language, {
        ko: '관리자 화면 다시 열기',
        en: 'Reopen admin page',
        ja: '管理画面を開き直す',
      }),
    };
    return (
      <div className="auth-session-shell is-compact">
        <aside
          aria-label={recoveryText.status}
          className="auth-impersonation-banner"
          data-testid="impersonation-sync-recovery"
        >
          <div className="auth-impersonation-copy">
            <strong className="auth-impersonation-status">
              {recoveryText.status}
            </strong>
            <p role="alert">{recoveryText.description}</p>
          </div>
          <div className="auth-impersonation-actions">
            <a className="auth-impersonation-button" href="/admin/users">
              {recoveryText.action}
            </a>
          </div>
        </aside>
      </div>
    );
  }

  if (!visible || !user) {
    return null;
  }

  const isStudioListRoute = location.pathname === '/studio';
  const isStudioDetailRoute = location.pathname.startsWith('/studio/');
  const isStudioRoute = isStudioListRoute || isStudioDetailRoute;
  // 라이브러리 링크는 cross-route 에서만 노출 — `/studio`, `/studio/:id` 에서는
  // page header `<` 뒤로 가 owner.
  const showLibraryLink = !isStudioRoute;
  const text = {
    sessionLabel: pickLocalized(language, {
      ko: '계정 메뉴',
      en: 'Account menu',
      ja: 'アカウントメニュー',
    }),
    account: pickLocalized(language, {
      ko: '계정',
      en: 'Account',
      ja: 'アカウント',
    }),
    library: pickLocalized(language, {
      ko: '라이브러리',
      en: 'Library',
      ja: 'ライブラリ',
    }),
    logout: pickLocalized(language, {
      ko: '로그아웃',
      en: 'Log out',
      ja: 'ログアウト',
    }),
    loggingOut: pickLocalized(language, {
      ko: '로그아웃 중…',
      en: 'Logging out…',
      ja: 'ログアウト中…',
    }),
    logoutError: pickLocalized(language, {
      ko: '로그아웃에 실패했습니다. 잠시 후 다시 시도해주세요.',
      en: 'Log out failed. Please try again in a moment.',
      ja: 'ログアウトに失敗しました。しばらくしてからもう一度お試しください。',
    }),
    impersonationStatus: pickLocalized(language, {
      ko: '지원용 로그인 중',
      en: 'Support session active',
      ja: 'サポート用ログイン中',
    }),
    impersonationOrientation: (identity: string) => pickLocalized(language, {
      ko: `지금 ${identity} 계정으로 보고 있습니다.`,
      en: `You are currently viewing the ${identity} account.`,
      ja: `現在、${identity} のアカウントを表示しています。`,
    }),
    impersonationContext: (identity: string) => pickLocalized(language, {
      ko: `원래 관리자: ${identity}. 대리 로그인은 최대 30분 동안 유지됩니다.`,
      en: `Original admin: ${identity}. This session lasts for up to 30 minutes.`,
      ja: `元の管理者: ${identity}。代理ログインは最大30分間維持されます。`,
    }),
    returnToAdmin: pickLocalized(language, {
      ko: '관리자로 돌아가기',
      en: 'Return to admin',
      ja: '管理者に戻る',
    }),
    returningToAdmin: pickLocalized(language, {
      ko: '돌아가는 중…',
      en: 'Returning…',
      ja: '戻っています…',
    }),
    returnError: pickLocalized(language, {
      ko: '관리자 계정으로 돌아가지 못했습니다. 현재 사용자 세션은 그대로 유지됩니다.',
      en: 'Could not return to the admin account. The current user session is unchanged.',
      ja: '管理者アカウントに戻れませんでした。現在のユーザーセッションは維持されます。',
    }),
    remoteSessionNotice: pickLocalized(language, {
      ko: '이 브라우저에는 원래 관리자 세션이 없습니다. 지원을 마치면 세션을 종료해 주세요.',
      en: 'This browser does not have the original admin session. End this session when support is complete.',
      ja: 'このブラウザには元の管理者セッションがありません。サポートが終わったら、このセッションを終了してください。',
    }),
    endSupportSession: pickLocalized(language, {
      ko: '지원 로그인 종료',
      en: 'End support session',
      ja: 'サポートログインを終了',
    }),
    endingSupportSession: pickLocalized(language, {
      ko: '종료 중…',
      en: 'Ending…',
      ja: '終了中…',
    }),
  };
  const errorMessage = failure
    ? localizeRequestError(language, failure.error, failure.action === 'logout' ? text.logoutError : text.returnError)
    : null;
  const impersonation = user.impersonation?.active ? user.impersonation : null;
  const targetIdentity = user.name?.trim()
    ? `${user.name.trim()} (${user.email})`
    : user.email;
  const actorIdentity = impersonation?.actor.name?.trim()
    ? `${impersonation.actor.name.trim()} (${impersonation.actor.email})`
    : (impersonation?.actor.email ?? '');

  async function handleLogout() {
    setFailure(null);
    try {
      await logoutMutation.mutateAsync();
      navigate('/login', { replace: true });
    } catch (error) {
      setFailure({ action: 'logout', error });
    }
  }

  async function handleReturnToAdmin() {
    setFailure(null);
    try {
      await stopImpersonationMutation.mutateAsync();
      navigate('/admin/users', { replace: true });
    } catch (error) {
      if (error instanceof ImpersonationSessionSyncError) {
        setRequiresAdminSyncRecovery(true);
        return;
      }
      setFailure({ action: 'return', error });
    }
  }

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <div className="auth-session-shell is-compact">
      {impersonation ? (
        <aside
          aria-label={text.impersonationStatus}
          className="auth-impersonation-banner"
          data-testid="impersonation-banner"
        >
          <div className="auth-impersonation-copy">
            <strong className="auth-impersonation-status" role="status">
              {text.impersonationStatus}
            </strong>
            <p>{text.impersonationOrientation(targetIdentity)}</p>
            <p className="auth-impersonation-context">
              {text.impersonationContext(actorIdentity)}
            </p>
            {!impersonation.can_return_to_admin ? (
              <p className="auth-impersonation-remote-copy">
                {text.remoteSessionNotice}
              </p>
            ) : null}
          </div>
          <div className="auth-impersonation-actions">
            {impersonation.can_return_to_admin ? (
              <button
                className="auth-impersonation-button"
                disabled={stopImpersonationMutation.isPending}
                type="button"
                onClick={() => void handleReturnToAdmin()}
              >
                {stopImpersonationMutation.isPending
                  ? text.returningToAdmin
                  : text.returnToAdmin}
              </button>
            ) : (
              <button
                className="auth-impersonation-button"
                disabled={logoutMutation.isPending}
                type="button"
                onClick={() => void handleLogout()}
              >
                {logoutMutation.isPending
                  ? text.endingSupportSession
                  : text.endSupportSession}
              </button>
            )}
          </div>
          {errorMessage ? (
            <p className="auth-impersonation-error" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </aside>
      ) : null}
      <section
        className="auth-session-bar is-compact"
        data-testid="auth-session-bar"
        data-variant="compact"
        aria-label={text.sessionLabel}
      >
        {theme === 'asome-guitar' ? (
          <Link to="/studio" className="account-theme-brand" aria-label="어썸기타 연습실">
            <img src="/img/asome-guitar-wordmark.png" width="248" height="22" alt="ASOME GUITAR" />
            <span>어썸기타 연습실</span>
          </Link>
        ) : null}
        <div className="auth-session-account-menu" ref={menuRef}>
          <button
            type="button"
            ref={triggerRef}
            className="auth-session-account-trigger"
            aria-label={text.sessionLabel}
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
            aria-controls={menuId}
            data-testid="auth-session-account-trigger"
            onClick={() => setMenuOpen((value) => !value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setMenuOpen(true);
              }
            }}
          >
            <span>{text.account}</span>
            <span className="auth-session-account-caret" aria-hidden="true">
              ▾
            </span>
          </button>

          {menuOpen ? (
            <div
              id={menuId}
              className="auth-session-account-popover"
              role="group"
              aria-label={text.sessionLabel}
              data-testid="auth-session-account-popover"
            >
              <LanguageSwitcher />
              {showLibraryLink ? (
                <Link to="/studio" className="auth-session-link" onClick={closeMenu}>
                  {text.library}
                </Link>
              ) : null}
              <button
                type="button"
                className="auth-session-link auth-session-link-logout"
                onClick={() => {
                  closeMenu();
                  void handleLogout();
                }}
                disabled={logoutMutation.isPending}
              >
                {logoutMutation.isPending ? text.loggingOut : text.logout}
              </button>
            </div>
          ) : null}
        </div>

        {errorMessage && !impersonation ? (
          <p className="auth-session-error" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </section>
    </div>
  );
}
